/**
 * Facebook Pixel tracking utilities for FlowFrame
 */

import { getAnalytics, logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../firebase";

const functions = getFunctions();
const fbConversionAPI = httpsCallable(functions, 'fbConversionAPI');

/**
 * Track a standard event with the Facebook Pixel
 * @param {string} eventName - Standard event name (e.g., 'Purchase', 'CompleteRegistration')
 * @param {Object} eventParams - Additional parameters to include with the event
 * @param {Object} options - Additional options for tracking
 */
export function trackEvent(eventName, eventParams = {}, options = {}) {
    const { onlyFirebase = false, onlyFacebook = false } = options;
  
    try {
      // Track with Firebase Analytics
      if (!onlyFacebook && analytics) {
        logEvent(analytics, eventName.toLowerCase(), eventParams);
        console.log(`[Firebase Analytics] Tracked: ${eventName.toLowerCase()}`, eventParams);
      }
      
      // Track with Facebook Pixel
     // if (!onlyFirebase && window.fbq) {
     //   window.fbq('track', eventName, eventParams);
     //   console.log(`[Facebook Pixel] Tracked: ${eventName}`, eventParams);
     // }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
}

/**
 * Track a custom event with the Facebook Pixel
 * @param {string} eventName - Custom event name
 * @param {Object} eventParams - Additional parameters to include with the event
 */
export const trackCustomEvent = (eventName, eventParams = {}) => {
  if (window.fbq) {
    window.fbq('trackCustom', eventName, eventParams);
    console.log(`[FB Pixel] Tracked custom event: ${eventName}`, eventParams);
  } else {
    console.warn('Facebook Pixel not loaded');
  }
};

// Common event tracking functions for FlowFrame

/**
 * Track subscription purchase
 * @param {string} tier - Subscription tier (free, basic, pro)
 * @param {number} value - Purchase value
 */
export const trackSubscription = (tier, value) => {
  trackEvent('Purchase', {
    content_name: `FlowFrame ${tier} Subscription`,
    content_type: 'subscription',
    content_ids: [`subscription_${tier}`],
    value: value,
    currency: 'USD'
  });
};

/**
 * Track user registration
 * @param {string} method - Registration method (email, google)
 */
export const trackRegistration = (method) => {
  trackEvent('CompleteRegistration', {
    content_name: 'FlowFrame Account',
    registration_method: method
  });
};

/**
 * Track pattern creation
 * @param {Object} pattern - The pattern details
 */
export const trackPatternCreation = (pattern) => {
  trackCustomEvent('PatternCreated', {
    pattern_type: pattern.folder || 'Uncategorized',
    measure_count: pattern.measures?.length || 0,
    has_tuplets: pattern.measures?.some(m => m.tuplets?.length > 0) || false
  });
};

/**
 * Track practice session
 * @param {number} duration - Session duration in minutes
 */
export const trackPracticeSession = (duration) => {
  trackCustomEvent('PracticeSessionCompleted', {
    duration_minutes: duration
  });
};

/**
 * Track premium feature interaction (for free users)
 * @param {string} featureName - Name of the premium feature
 */
export const trackPremiumFeatureInteraction = (featureName) => {
  trackCustomEvent('PremiumFeatureInteraction', {
    feature_name: featureName
  });
};

// For tracking errors specifically
export function trackError(errorType, errorMessage, isFatal = false) {
  trackEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage,
    was_fatal: isFatal
  });
}

// Implementation for each event from the image

// 1. View Content Event
export const trackViewContent = (contentData) => {
  if (window.fbq) {
    window.fbq('track', 'ViewContent', contentData);
  }
  
  // Skip server-side tracking for now
  // sendServerSideEvent('ViewContent', contentData);
};

// 2. Start Trial Event
export const trackStartTrial = (trialData) => {
  if (window.fbq) {
    window.fbq('track', 'StartTrial', trialData);
  }
  
  // Skip server-side tracking for now
  // sendServerSideEvent('StartTrial', trialData);
};

// 3. Lead Event
export const trackLead = (leadData) => {
  if (window.fbq) {
    window.fbq('track', 'Lead', leadData);
  }
  
  // Skip server-side tracking for now
  // sendServerSideEvent('Lead', leadData);
};

// 4. Login Event
export const trackLogin = (loginMethod = 'email') => {
  const loginData = { method: loginMethod };
  
  if (window.fbq) {
    window.fbq('track', 'Login', loginData);
  }
  
  // Skip server-side tracking for now
  // sendServerSideEvent('Login', loginData);
};

// 5. Customize Product Event
export const trackCustomizeProduct = (productData) => {
  if (window.fbq) {
    window.fbq('track', 'CustomizeProduct', productData);
  }
  
  // Skip server-side tracking for now
  // sendServerSideEvent('CustomizeProduct', productData);
};

// Helper function to send events to server-side CAPI
async function sendServerSideEvent(eventName, eventData) {
  try {
    // Get user data based on event requirements from the image
    const userData = await collectUserData(eventName);
    
    await fbConversionAPI({
      eventName,
      eventData: {
        ...eventData,
        sourceUrl: window.location.href
      },
      userData
    });
  } catch (error) {
    console.error('Error sending server-side event:', error);
  }
}

// Collect user data based on event type
async function collectUserData(eventName) {
  const currentUser = auth.currentUser;
  
  // Basic data for all events
  const userData = {
    userAgent: navigator.userAgent
  };
  
  // Add authenticated user data if available
  if (currentUser) {
    userData.userId = currentUser.uid;
    userData.email = currentUser.email || '';
    
    // Get additional user profile data if available
    const userProfile = await getUserProfile(currentUser.uid);
    if (userProfile) {
      // Add data based on event type
      if (eventName === 'CustomizeProduct') {
        // All parameters for CustomizeProduct
        userData.firstName = userProfile.firstName || userProfile.displayName || '';
        userData.lastName = userProfile.lastName || '';
        userData.phone = userProfile.phone || userProfile.phoneNumber || '';
        userData.dateOfBirth = userProfile.dateOfBirth || '';
        userData.city = userProfile.city || '';
        userData.zipCode = userProfile.zipCode || userProfile.postalCode || '';
        userData.country = userProfile.country || '';
      } else if (['Login', 'Lead'].includes(eventName)) {
        // Email for Login and Lead events
        userData.email = userProfile.email || currentUser.email || '';
      }
    }
  }
  
  return userData;
}

// Helper function to get user profile data from Firestore
async function getUserProfile(userId) {
  try {
    // Import Firebase modules
    const { getFirestore, doc, getDoc } = require('firebase/firestore');
    const db = getFirestore();
    
    // Get user document from Firestore
    const userDoc = await getDoc(doc(db, "users", userId));
    
    if (userDoc.exists()) {
      return userDoc.data();
    } else {
      console.log("No user profile found for ID:", userId);
      return null;
    }
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return null;
  }
} 

/**
 * Extract features used in a pattern
 * @param {Array} measures - The measures to analyze
 * @param {boolean} muteRandomBeats - Global setting for random mute
 * @returns {Array} List of features used in the pattern
 */
export function getPatternFeatures(measures, muteRandomBeats = false) {
    const features = [];
    
    let hasTuplets = false;
    let hasDrones = false;
    let hasModulation = false;
    let hasRandomMute = false;
    let hasHarmony = false;
    
    // Check for global features
    if (muteRandomBeats) hasRandomMute = true;
    
    // Check measure-specific features
    measures.forEach(measure => {
      if (measure.tuplets?.length > 0) hasTuplets = true;
      if (measure.hasModulation) hasModulation = true;
      
      measure.beatSounds?.forEach(beat => {
        if (beat.dronePitchNote !== null) hasDrones = true;
        if (beat.hasHarmony) hasHarmony = true;
      });
    });
    
    if (hasTuplets) features.push("tuplet");
    if (hasDrones) features.push("drone");
    if (hasHarmony) features.push("harmony");
    if (hasModulation) features.push("metricModulation");
    if (hasRandomMute) features.push("muteRandom");
    
    return features;
  }
  
  /**
   * Calculate pattern complexity score (1-10)
   * @param {Array} measures - The measures to analyze
   * @returns {number} Complexity score (1-10)
   */
  export function getPatternComplexity(measures) {
    let complexity = 1; // Base complexity
    
    // Increase complexity based on:
    // 1. Number of measures
    complexity += Math.min(3, measures.length / 4); // Max +3 for many measures
    
    // 2. Time signature variety
    const uniqueTimeSignatures = new Set(
      measures.map(m => `${m.numerator}/${m.denominator}`)
    );
    complexity += uniqueTimeSignatures.size - 1; // +1 for each additional time sig
    
    // 3. Advanced features (reuse our feature detection)
    const features = getPatternFeatures(measures);
    if (features.includes("tuplet")) complexity += 2;
    if (features.includes("metricModulation")) complexity += 2;
    if (features.includes("drone")) complexity += 1;
    if (features.includes("harmony")) complexity += 1;
    
    return Math.min(10, Math.round(complexity)); // Cap at 10
  }
  
  /**
   * Get unique time signatures used in a pattern
   * @param {Array} measures - The measures to analyze
   * @returns {Array} List of time signatures in format ["4/4", "7/8"]
   */
  export function getTimeSignaturesUsed(measures) {
    return [...new Set(
      measures.map(m => `${m.numerator}/${m.denominator}`)
    )];
  }

// Add these new functions to your existing analytics.js file

// Session tracking state
let sessionStartTime = null;
let lastActivityTime = null;
let sessionCheckInterval = null;

/**
 * Track user login with enhanced data
 * @param {string} method - Login method (email, google)
 * @param {Object} user - Firebase user object
 */
export const trackUserLogin = (method, user) => {
  const loginTime = new Date();
  sessionStartTime = loginTime;
  lastActivityTime = loginTime;
  
  // Store session info in localStorage for persistence
  localStorage.setItem('sessionStartTime', loginTime.getTime().toString());
  localStorage.setItem('lastActivityTime', loginTime.getTime().toString());
  
  // Track the login event
  trackEvent('user_login', {
    method: method,
    user_id: user.uid,
    login_time: loginTime.toISOString(),
    subscription_tier: localStorage.getItem('subscriptionTier') || 'free',
    is_returning_user: localStorage.getItem('hasLoggedInBefore') === 'true'
  });
  
  // Mark that user has logged in before
  localStorage.setItem('hasLoggedInBefore', 'true');
  
  // Start activity monitoring
  startActivityMonitoring();
  
  console.log(`[Session] User logged in via ${method} at ${loginTime.toISOString()}`);
};

/**
 * Track user logout with session duration
 * @param {Object} user - Firebase user object (if available)
 */
export const trackUserLogout = (user = null) => {
  const logoutTime = new Date();
  const sessionDuration = calculateSessionDuration();
  
  trackEvent('user_logout', {
    user_id: user?.uid || 'unknown',
    logout_time: logoutTime.toISOString(),
    session_duration_minutes: Math.round(sessionDuration / 60),
    session_duration_seconds: sessionDuration,
    subscription_tier: localStorage.getItem('subscriptionTier') || 'free'
  });
  
  // Clean up session tracking
  stopActivityMonitoring();
  clearSessionData();
  
  console.log(`[Session] User logged out. Session duration: ${Math.round(sessionDuration / 60)} minutes`);
};

/**
 * Calculate current session duration in seconds
 */
export const calculateSessionDuration = () => {
  const startTime = localStorage.getItem('sessionStartTime');
  if (!startTime) return 0;
  
  const now = new Date().getTime();
  return Math.floor((now - parseInt(startTime)) / 1000);
};

/**
 * Track user activity to detect idle sessions
 */
const updateLastActivity = () => {
  lastActivityTime = new Date();
  localStorage.setItem('lastActivityTime', lastActivityTime.getTime().toString());
};

/**
 * Start monitoring user activity
 */
const startActivityMonitoring = () => {
  // Track various user interactions
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  
  events.forEach(event => {
    document.addEventListener(event, updateLastActivity, true);
  });
  
  // Check for idle sessions every 5 minutes
  sessionCheckInterval = setInterval(() => {
    checkForIdleSession();
  }, 5 * 60 * 1000); // 5 minutes
};

/**
 * Stop monitoring user activity
 */
const stopActivityMonitoring = () => {
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  
  events.forEach(event => {
    document.removeEventListener(event, updateLastActivity, true);
  });
  
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval);
    sessionCheckInterval = null;
  }
};

/**
 * Check if user has been idle for too long
 */
const checkForIdleSession = () => {
  const lastActivity = localStorage.getItem('lastActivityTime');
  if (!lastActivity) return;
  
  const now = new Date().getTime();
  const idleTime = (now - parseInt(lastActivity)) / 1000 / 60; // in minutes
  
  // If idle for more than 30 minutes, track idle session
  if (idleTime > 30) {
    trackEvent('session_idle', {
      idle_duration_minutes: Math.round(idleTime),
      total_session_duration_minutes: Math.round(calculateSessionDuration() / 60)
    });
  }
};

/**
 * Clear session data from localStorage
 */
const clearSessionData = () => {
  localStorage.removeItem('sessionStartTime');
  localStorage.removeItem('lastActivityTime');
  sessionStartTime = null;
  lastActivityTime = null;
};

/**
 * Track session heartbeat (called periodically to track active sessions)
 */
export const trackSessionHeartbeat = () => {
  const sessionDuration = calculateSessionDuration();
  
  // Only track if session is active and longer than 1 minute
  if (sessionDuration > 60) {
    trackEvent('session_heartbeat', {
      session_duration_minutes: Math.round(sessionDuration / 60),
      current_tab: document.hidden ? 'background' : 'active',
      subscription_tier: localStorage.getItem('subscriptionTier') || 'free'
    });
  }
};

/**
 * Initialize session tracking on page load (for existing sessions)
 */
export const initializeSessionTracking = (user) => {
  const existingSessionStart = localStorage.getItem('sessionStartTime');
  
  if (existingSessionStart && user) {
    // Resume existing session tracking
    sessionStartTime = new Date(parseInt(existingSessionStart));
    lastActivityTime = new Date(parseInt(localStorage.getItem('lastActivityTime') || existingSessionStart));
    startActivityMonitoring();
    
    console.log('[Session] Resumed existing session tracking');
  }
};